import { useState } from 'react'
import { useTelegram } from '@/hooks/useTelegram'
import { useAuthStore } from '@/store/authStore'
import { loginWithTelegram } from '@/services/auth'
import api from '@/services/api'
import Button from '@/components/ui/Button'

export default function LoginPage() {
  const { initData, isTelegramApp } = useTelegram()
  const { setAuth } = useAuthStore()
  const [error, setError] = useState<string | null>(null)
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  // Dev mode state
  const [devTelegramId, setDevTelegramId] = useState('')

  const handleLogin = async () => {
    if (!initData) {
      setError('Откройте приложение через Telegram')
      return
    }

    setIsLoggingIn(true)
    setError(null)

    try {
      const response = await loginWithTelegram(initData)
      setAuth({
        user: response.user,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      })
    } catch (err: any) {
      console.error('Login error:', err)
      setError(err.response?.data?.detail || 'Ошибка авторизации')
    } finally {
      setIsLoggingIn(false)
    }
  }

  // Dev login handler
  const handleDevLogin = async () => {
    if (!devTelegramId) {
      setError('Введите Telegram ID')
      return
    }

    setIsLoggingIn(true)
    setError(null)

    try {
      const response = await api.post('/auth/dev-login', null, {
        params: { telegram_id: parseInt(devTelegramId) },
      })
      setAuth({
        user: response.data.user,
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
      })
    } catch (err: any) {
      console.error('Dev login error:', err)
      setError(err.response?.data?.detail || 'Ошибка dev авторизации')
    } finally {
      setIsLoggingIn(false)
    }
  }

  return (
    <div className="min-h-screen bg-tg-bg flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm text-center">
        {/* Логотип / Заголовок */}
        <div className="mb-8">
          <div className="text-6xl mb-4">🏋️</div>
          <h1 className="text-2xl font-bold text-tg-text mb-2">
            Trainer App
          </h1>
          <p className="text-tg-hint">
            Управление расписанием тренировок
          </p>
        </div>

        {/* Ошибка */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Кнопка входа */}
        {isTelegramApp ? (
          <Button
            onClick={handleLogin}
            loading={isLoggingIn}
            fullWidth
            size="lg"
          >
            Войти через Telegram
          </Button>
        ) : (
          <div className="text-tg-hint text-sm">
            <p className="mb-4">
              Для входа откройте приложение через Telegram бота
            </p>
            <p className="text-xs opacity-70">
              Режим разработки: Telegram WebApp недоступен
            </p>
          </div>
        )}

        {/* Dev Mode форма - только в development */}
        {import.meta.env.DEV && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-3 font-medium">
              Dev Mode (только для тестирования)
            </p>
            <input
              type="number"
              inputMode="numeric"
              placeholder="Telegram ID (например: 123456789)"
              value={devTelegramId}
              onChange={(e) => setDevTelegramId(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black"
              style={{ WebkitUserSelect: 'text', userSelect: 'text' }}
              autoComplete="off"
            />
            <Button
              onClick={handleDevLogin}
              loading={isLoggingIn}
              fullWidth
              variant="secondary"
            >
              Dev Login
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
